/**
 * Pure plan of where a verse can legally land — drives board drop wash.
 */
import {
  canPlaceInSlot,
  LAYER_ORDER,
  SLOT_COUNT,
  type BoardState,
} from "../board/board";
import type { LayerId } from "../board/types";
import { isLieRedeemable } from "../combat/redeemable";
import type { VerseDef } from "./types";

export type DropWash =
  | "global"
  | "layers"
  | "enemies"
  | "allies"
  | "place"
  | "convert"
  | "self";

export type LegalTargetPlan = {
  wash: DropWash;
  /** Slot keys `layer:slotIndex` that accept this card. */
  slotKeys: string[];
  /** Layers that accept a layer-aim drop (chip / still / layer prefer). */
  layerKeys: LayerId[];
  /** Short cue for empty-slot badges during place wash. */
  emptyHint: string;
  /** Cue for focused presence. */
  presenceHint: string;
};

export function slotKey(layer: LayerId, slot: number): string {
  return `${layer}:${slot}`;
}

export function planLegalTargets(
  board: BoardState,
  def: VerseDef,
): LegalTargetPlan {
  if (def.kind === "placeable") {
    const slotKeys: string[] = [];
    for (const layer of LAYER_ORDER) {
      for (let s = 0; s < SLOT_COUNT; s++) {
        if (canPlaceInSlot(board, layer, s, "player")) {
          slotKeys.push(slotKey(layer, s));
        }
      }
    }
    // Prefer layers that have a legal place slot
    const layerKeys = LAYER_ORDER.filter((L) =>
      slotKeys.some((k) => k.startsWith(`${L}:`)),
    );
    return {
      wash: "place",
      slotKeys,
      layerKeys,
      emptyHint: "Plant",
      presenceHint: "Replace",
    };
  }

  const sel = def.selector ?? "strongestLie";

  if (sel === "empower" || sel === "self") {
    return {
      wash: "self",
      slotKeys: [],
      layerKeys: [...LAYER_ORDER],
      emptyHint: "Drop",
      presenceHint: "",
    };
  }

  if (sel === "restorePrayer") {
    const slotKeys: string[] = [];
    for (const layer of LAYER_ORDER) {
      board[layer].slots.forEach((p, s) => {
        if (!p || p.side !== "player") return;
        // Matches Game.pickDamagedPrayer: level below max
        if (p.level < p.maxLevel) {
          slotKeys.push(slotKey(layer, s));
        }
      });
    }
    // Fallback: any Prayer so they still see “drag onto Prayer”
    if (slotKeys.length === 0) {
      for (const layer of LAYER_ORDER) {
        board[layer].slots.forEach((p, s) => {
          if (p?.side === "player") slotKeys.push(slotKey(layer, s));
        });
      }
    }
    return {
      wash: "allies",
      slotKeys,
      layerKeys: LAYER_ORDER.filter((L) =>
        slotKeys.some((k) => k.startsWith(`${L}:`)),
      ),
      emptyHint: "",
      presenceHint: "Mend",
    };
  }

  if (sel === "gatherPrayers") {
    type PRow = {
      level: number;
      maxLevel: number;
      side: string;
    };
    const prayers: { p: PRow; layer: LayerId; s: number }[] = [];
    for (const layer of LAYER_ORDER) {
      board[layer].slots.forEach((p, s) => {
        if (p?.side === "player" && p.level > 0) {
          prayers.push({ p, layer, s });
        }
      });
    }
    const residualCount = prayers.filter((x) => x.p.level === 1).length;
    const slotKeys: string[] = [];
    for (const { p, layer, s } of prayers) {
      // Host below max; need at least one *other* residual I as donor
      if (p.level >= p.maxLevel) continue;
      const otherI = residualCount - (p.level === 1 ? 1 : 0);
      if (otherI >= 1) slotKeys.push(slotKey(layer, s));
    }
    // Fallback: damaged prayers so wash still teaches “drag onto a keep”
    if (slotKeys.length === 0) {
      for (const { p, layer, s } of prayers) {
        if (p.level < p.maxLevel) slotKeys.push(slotKey(layer, s));
      }
    }
    return {
      wash: "allies",
      slotKeys,
      layerKeys: LAYER_ORDER.filter((L) =>
        slotKeys.some((k) => k.startsWith(`${L}:`)),
      ),
      emptyHint: "",
      presenceHint: "Gather",
    };
  }

  if (sel === "convertLie") {
    const slotKeys: string[] = [];
    for (const layer of LAYER_ORDER) {
      board[layer].slots.forEach((p, s) => {
        if (p?.side === "enemy" && isLieRedeemable(p)) {
          slotKeys.push(slotKey(layer, s));
        }
      });
    }
    return {
      wash: "convert",
      slotKeys,
      layerKeys: LAYER_ORDER.filter((L) =>
        slotKeys.some((k) => k.startsWith(`${L}:`)),
      ),
      emptyHint: "",
      presenceHint: "Redeem",
    };
  }

  if (
    sel === "layerChip" ||
    sel === "layerSlam" ||
    sel === "slowLayer"
  ) {
    const locked = def.lockLayer;
    const layerKeys: LayerId[] = locked
      ? [locked]
      : def.preferLayer
        ? [
            def.preferLayer,
            ...LAYER_ORDER.filter((L) => L !== def.preferLayer),
          ]
        : [...LAYER_ORDER];
    // All slots on those layers accept a layer drop
    const slotKeys: string[] = [];
    for (const layer of layerKeys) {
      for (let s = 0; s < SLOT_COUNT; s++) {
        slotKeys.push(slotKey(layer, s));
      }
    }
    return {
      wash: "layers",
      slotKeys,
      layerKeys,
      emptyHint: "Layer",
      presenceHint: "Layer",
    };
  }

  // Answers / softens / slows: enemy-bearing slots (global may also auto-pick)
  const slotKeys: string[] = [];
  for (const layer of LAYER_ORDER) {
    board[layer].slots.forEach((p, s) => {
      if (p?.side === "enemy") slotKeys.push(slotKey(layer, s));
    });
  }

  const wash: DropWash =
    def.respectMask === "autoOnly" ||
    sel === "strongestLie" ||
    sel === "innermostLie" ||
    sel === "typePriority"
      ? "global"
      : "enemies";

  return {
    wash,
    slotKeys,
    layerKeys: LAYER_ORDER.filter((L) =>
      slotKeys.some((k) => k.startsWith(`${L}:`)),
    ),
    emptyHint: "",
    presenceHint: sel === "slowLie" ? "Still" : "Answer",
  };
}
